// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// What actually happens to people. Nature of injury, the part of the body, the
// mechanism, and who it happens to — each ranked by COUNT and by COST, because
// the two orders are very different and only showing one is misleading.

import { rankedBars, sparkline } from './charts';
import { squarify } from '../utils/squarify';
import { card, legend, note, segmented, viewHead } from './ui';
import { gloss } from '../glossary';
import { esc, money, num, period, tipAttr, weeks } from '../format';
import { COST, COUNT_STOPS, PHYSICAL, PSYCH, TIME, countColor } from '../colors';
import { totalOf } from '../data';
import type { Bundle, ClaimRow, Val } from '../types';

export const title = 'What gets hurt';

let rankBy: 'count' | 'comp' | 'lost' = 'count';

/**
 * A median computed over a handful of claims is noise. Safe Work Australia
 * publishes one as soon as five claims exist, so without a floor the "most
 * expensive body part" list is topped by categories with 15 and even 6 claims
 * behind them.
 */
const MIN_CLAIMS = 100;

function idxFor(rows: ClaimRow[], key: 'count' | 'comp' | 'lost', periods: string[]): number {
  for (let i = periods.length - 1; i >= 0; i--) if (rows.some((r) => r[key]?.[i] !== null && r[key]?.[i] !== undefined)) return i;
  return periods.length - 1;
}

function display(v: Val, key: 'count' | 'comp' | 'lost'): string {
  return key === 'count' ? num(v) : key === 'comp' ? money(v) : weeks(v);
}

export function render(b: Bundle): string {
  const { claims } = b;
  const P = claims.periods;
  const n = P.length;

  const natureLeaves = claims.nature.filter((r) => r.level === 2 && !r.total && !r.nfd);
  const bodyMajors = claims.body.filter((r) => r.level === 1 && !r.total);
  const bodyLeaves = claims.body.filter((r) => r.level === 2 && !r.total && !r.nfd);
  const mechMajors = claims.mechanism.filter((r) => r.level === 1 && !r.total);
  const demo = claims.demographics.filter((r) => r.level === 1 && !r.total);
  const total = totalOf(claims.nature)!;

  const idx = idxFor(natureLeaves, rankBy, P);
  const rankedNature = natureLeaves
    .filter((r) => r[rankBy]?.[idx] !== null && r[rankBy]?.[idx] !== undefined)
    .sort((a, b2) => (b2[rankBy]![idx] as number) - (a[rankBy]![idx] as number))
    .slice(0, 12);

  // Treemap of the body: majors sized by claim count. The squarified layout
  // comes from the factory pattern, not a re-roll.
  const bodyIdx = n - 1;
  const bodyVals = bodyMajors.map((r) => r.count[bodyIdx] ?? 0);
  const TW = 840;
  const TH = 300;
  const rects = squarify([...bodyVals].sort((a, c) => c - a), TW, TH);
  const sortedBody = [...bodyMajors].sort((a, c) => (c.count[bodyIdx] ?? 0) - (a.count[bodyIdx] ?? 0));
  const bodyTotal = bodyVals.reduce((a, c) => a + c, 0) || 1;

  const mechIdx = n - 1;
  const mechTotal = mechMajors.reduce((a, r) => a + (r.count[mechIdx] ?? 0), 0) || 1;

  const demoLostIdx = idxFor(demo, 'lost', P);

  return `
    ${viewHead(
      'What gets hurt',
      'What actually happens to injured Australian workers?',
      `The ${gloss('TOOCS', 'toocs')} coding system describes each claim four separate ways — what is wrong with the person, where on their body, what action caused it, and what object was involved. Ranking the same injuries by how <em>often</em> they happen and by what they <em>cost</em> produces two very different lists.`,
    )}

    <div class="ctl-row">
      ${segmented('rank', [
        { value: 'count', label: 'By how often' },
        { value: 'comp', label: 'By cost' },
        { value: 'lost', label: 'By time off work' },
      ], rankBy)}
      <span class="ctl-note">${period(P[idx])}${rankBy !== 'count' ? ` · the newest year is withheld on cost measures` : ''}</span>
    </div>

    ${card(
      'Nature of the injury',
      rankBy === 'count'
        ? 'The most common things that go wrong. Soft-tissue and joint injuries dominate the count.'
        : 'The most expensive things that go wrong. This list barely resembles the one ordered by frequency — which is the point.',
      rankedBars(
        rankedNature.map((r) => ({
          label: r.name,
          value: r[rankBy]![idx],
          display: display(r[rankBy]![idx], rankBy),
          color: r.code === 'I' ? PSYCH : PHYSICAL,
          sub: rankBy === 'count' ? undefined : `${num(r.count[idx])} claims`,
          tip: `${r.name}\n${num(r.count[idx])} serious claims in ${period(P[idx])}\nMedian compensation ${money(r.comp?.[idx] ?? null)}\nMedian time lost ${weeks(r.lost?.[idx] ?? null)}`,
        })),
      ) +
        legend([
          { color: PHYSICAL, label: 'Physical injury or disease' },
          { color: PSYCH, label: 'Mental health condition' },
        ]),
    )}

    ${card(
      `Where on the body, ${period(P[bodyIdx])}`,
      'Every serious claim is coded to a body region. Area is proportional to the number of claims.',
      `<div class="chart">${`<svg viewBox="0 0 ${TW} ${TH}" class="chart-svg" role="img" aria-label="${esc(`Treemap of serious claims by body region: ${sortedBody.slice(0, 4).map((r) => `${r.name} ${num(r.count[bodyIdx])}`).join(', ')}`)}">${rects
        .map((rect, i) => {
          const row = sortedBody[i];
          if (!row) return '';
          const value = row.count[bodyIdx] ?? 0;
          const shade = countColor(value / Math.max(...bodyVals));
          const showLabel = rect.w > 66 && rect.h > 30;
          const tip = `${row.name}\n${num(value)} serious claims (${((value / bodyTotal) * 100).toFixed(1)}% of all)\nMedian compensation ${money(row.comp?.[bodyIdx] ?? null)}\nMedian time lost ${weeks(row.lost?.[bodyIdx] ?? null)}`;
          return `<g data-tip="${tipAttr(tip)}">
            <rect class="mark" x="${rect.x.toFixed(1)}" y="${rect.y.toFixed(1)}" width="${Math.max(0, rect.w - 1).toFixed(1)}" height="${Math.max(0, rect.h - 1).toFixed(1)}" fill="${shade}" stroke="#fff"/>
            ${showLabel ? `<text class="tm-lab" x="${(rect.x + 7).toFixed(1)}" y="${(rect.y + 17).toFixed(1)}">${esc(row.name)}</text><text class="tm-val" x="${(rect.x + 7).toFixed(1)}" y="${(rect.y + 32).toFixed(1)}">${num(value)}</text>` : ''}
          </g>`;
        })
        .join('')}</svg>`}</div>` +
        `<div class="legend"><span class="legend-item">Fewer<span class="ramp">${COUNT_STOPS.map((c) => `<i style="background:${c}"></i>`).join('')}</span>More claims</span></div>`,
    )}

    ${card(
      `The specific body parts that cost the most`,
      `Ranked by median compensation rather than count, ${period(P[idxFor(bodyLeaves, 'comp', P)])}. A hand injury is common; a spinal one is not, and the payouts reflect that. Only body parts with at least ${MIN_CLAIMS} claims that year are ranked — a median over a dozen claims is not a number worth putting at the top of a list.`,
      rankedBars(
        bodyLeaves
          .filter((r) => r.comp?.[idxFor(bodyLeaves, 'comp', P)] !== null && (r.count[idxFor(bodyLeaves, 'comp', P)] ?? 0) >= MIN_CLAIMS)
          .sort((a, c) => (c.comp![idxFor(bodyLeaves, 'comp', P)] as number) - (a.comp![idxFor(bodyLeaves, 'comp', P)] as number))
          .slice(0, 10)
          .map((r) => {
            const i = idxFor(bodyLeaves, 'comp', P);
            return {
              label: r.name,
              value: r.comp![i],
              display: money(r.comp![i]),
              color: COST,
              sub: `${num(r.count[i])} claims · ${weeks(r.lost?.[i] ?? null)} off work`,
              tip: `${r.name}\nMedian compensation ${money(r.comp![i])}\nMedian time lost ${weeks(r.lost?.[i] ?? null)}\n${num(r.count[i])} serious claims in ${period(P[i])}`,
            };
          }),
      ),
    )}

    ${card(
      `How it happened, ${period(P[mechIdx])}`,
      `The ${gloss('mechanism of incident', 'mechanism')} — the action or exposure that caused the injury, not the damage it did.`,
      rankedBars(
        [...mechMajors]
          .filter((r) => r.count[mechIdx] !== null)
          .sort((a, c) => (c.count[mechIdx] as number) - (a.count[mechIdx] as number))
          .map((r) => ({
            label: r.name,
            value: r.count[mechIdx],
            display: num(r.count[mechIdx]),
            color: r.code === '8' ? PSYCH : PHYSICAL,
            sub: `${(((r.count[mechIdx] ?? 0) / mechTotal) * 100).toFixed(1)}% of claims · median ${money(r.comp?.[mechIdx - 1] ?? null)}`,
            tip: `${r.name}\n${num(r.count[mechIdx])} serious claims (${(((r.count[mechIdx] ?? 0) / mechTotal) * 100).toFixed(1)}%)\nMedian compensation ${money(r.comp?.[mechIdx - 1] ?? null)} in ${period(P[mechIdx - 1])}\nMedian time lost ${weeks(r.lost?.[mechIdx - 1] ?? null)}`,
          })),
      ),
    )}

    ${card(
      `Who it happens to, and how long they are off`,
      `Serious claims and median time lost by age group. These are counts, not rates — there is no employment denominator by age anywhere in this release, so a group with more claims may simply have more workers in it. What the table <em>can</em> show is the time lost, which is a per-claim figure and rises steadily with age.`,
      `<div class="tbl-wrap"><table class="data" style="min-width:600px">
        <thead><tr><th scope="col">Age group</th><th scope="col" class="num">Serious claims</th><th scope="col" class="num">Median paid</th><th scope="col" class="num">Median time lost</th><th scope="col">Claims over the decade</th></tr></thead>
        <tbody>${demo
          .map(
            (r) => `<tr data-tip="${tipAttr(`${r.name}\n${num(r.count[n - 1])} serious claims in ${period(P[n - 1])}\nMedian time lost ${weeks(r.lost?.[demoLostIdx] ?? null)} in ${period(P[demoLostIdx])}`)}">
              <td>${esc(r.name)}</td>
              <td class="num">${num(r.count[n - 1])}</td>
              <td class="num">${money(r.comp?.[demoLostIdx] ?? null)}</td>
              <td class="num">${weeks(r.lost?.[demoLostIdx] ?? null)}</td>
              <td>${sparkline(r.count, TIME, 110, 26)}</td>
            </tr>`,
          )
          .join('')}
          <tr class="total-row"><td><strong>All ages</strong></td><td class="num">${num(total.count[n - 1])}</td><td class="num">${money(total.comp?.[demoLostIdx] ?? null)}</td><td class="num">${weeks(total.lost?.[demoLostIdx] ?? null)}</td><td>${sparkline(total.count, PHYSICAL, 110, 26)}</td></tr>
        </tbody></table></div>`,
    )}

    ${note(
      `<strong>Why there is no flow diagram here.</strong> It is tempting to draw the chain — what went wrong, how the body was hurt, which part was damaged — as one connected diagram. Safe Work Australia publishes those four codings as four <em>separate</em> tables of totals, with no record linking them. A flow diagram built from separate totals would show connections that were never measured, so this view shows them as what they are: four independent views of the same claims.`,
    )}
  `;
}

export function mount(root: HTMLElement, _b: Bundle, rerender: () => void): void {
  root.querySelectorAll<HTMLButtonElement>('[data-seg="rank"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const v = btn.dataset.value;
      rankBy = v === 'comp' ? 'comp' : v === 'lost' ? 'lost' : 'count';
      rerender();
    });
  });
}
